import { useRouteLocale } from "@/hooks/useRouteLocale";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import { Button } from "@/components/ui/button";
import { LanguageSwitcher } from "@/components/common/LanguageSwitcher";
import { UserProfileDropdown } from "@/components/common/UserProfileDropdown";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { usePlanSelectionStore, type PlanType } from "@/stores/planSelectionStore";
import { USER_ROUTES } from "@/routes";
import { PUBLIC_ROUTES } from "@/routes/public";
import { getCachedSubscriptionPlans, getSubscriptionPlans, type PlansApiResponse } from "@/services/api/subscriptions";
import { 
  Heart, 
  Users, 
  Gift, 
  Tablet, 
  Mail,
  Calendar, 
  Send, 
  ListChecks,
  Star,
  Check,
  Lock,
  Shield
} from "lucide-react";
import { FooterLinkColumns } from "@/components/common/FooterLinkColumns";

// NOTE: These are stored in /application/public/landing/ for reliable serving on jubiel.local and in deployments.
const heroTabletMockup = "/landing/hero-tablet-golden-serenity.png";
const heroMobileMockup = "/landing/hero-mobile-botez-bokeh.png";
const heroPaymentsStrip = "/landing/lovable-hero-accent-2.png";
const DEMO_EVENT_SLUG = "maria-si-vasile-wedding-demo";

export default function LandingPage() {
  const { t, currentLanguage } = useTranslation();
  const navigate = useNavigate();
  const urlLocale = useRouteLocale();

  usePageMeta({
    title: t("landing.metaTitle"),
    description: t("landing.metaDescription"),
    locale: urlLocale,
    path: PUBLIC_ROUTES.home(urlLocale),
    alternates: { ro: PUBLIC_ROUTES.home("ro"), en: PUBLIC_ROUTES.home("en") },
  });
  const { isAuthenticated, user } = useAuth();
  const { setSelectedPlan } = usePlanSelectionStore();
  // Ensure locale has a default value to prevent /undefined/ routes
  const currentLocale = urlLocale || 'ro';
  const localeCode = currentLanguage as 'ro' | 'en';
  const basePath = `/${currentLocale}`;
  const [plansData, setPlansData] = useState<PlansApiResponse | null>(null);

  const handleGetStarted = () => {
    setSelectedPlan('free', 'landing');
    navigate(`${basePath}/register`);
  };

  const handleViewExample = () => {
    navigate(PUBLIC_ROUTES.publicEvent(currentLocale, DEMO_EVENT_SLUG));
  };

  const handleSelectPlan = (plan: PlanType) => {
    setSelectedPlan(plan, 'landing');
    if (isAuthenticated) {
      // User is logged in, go to packages page (or checkout when free)
      navigate(plan === 'free' ? USER_ROUTES.events(currentLocale) : USER_ROUTES.packages(currentLocale));
    } else {
      // User not logged in, go to registration (plan will be pre-filled)
      navigate(`${basePath}/register`);
    }
  };

  const handleLogin = () => {
    if (isAuthenticated) {
      navigate(USER_ROUTES.events(currentLocale));
    } else {
      navigate(PUBLIC_ROUTES.login(currentLocale));
    }
  };

  const faqItems = useMemo(
    () => [
      {
        q: t("landing.faq.q1"),
        a: t("landing.faq.a1"),
      },
      {
        q: t("landing.faq.q2"),
        a: t("landing.faq.a2"),
      },
      {
        q: t("landing.faq.q3"),
        a: t("landing.faq.a3"),
      },
      {
        q: t("landing.faq.q4"),
        a: t("landing.faq.a4"),
      },
      {
        q: t("landing.faq.q5"),
        a: t("landing.faq.a5"),
      },
    ],
    [t, localeCode],
  );

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await getSubscriptionPlans();
        setPlansData(response);
      } catch (error) {
        console.error('LandingPage: Failed to fetch plans:', error);
        const cached = getCachedSubscriptionPlans();
        if (cached) setPlansData(cached);
      }
    };
    fetchPlans();
  }, []);

  const getPlanPrice = (plan: PlanType): string => {
    if (!plansData) return '—';
    const apiPlan = plansData.plans[plan];
    const priceTemplate = t(`landing.pricing.${plan}.priceTemplate`);
    return priceTemplate
      .replace('{{price}}', apiPlan.price.toString())
      .replace('{{currency}}', apiPlan.currency);
  };

  // Get plan features from API response and translate them
  const getPlanFeatures = (plan: PlanType): string[] => {
    if (!plansData) return [];
    const apiPlan = plansData.plans[plan];
    // Use the feature codes from the API response and translate them
    // First try landing.pricing.pachete section, then pricing.pachete
    return apiPlan.features.map((featureCode: string) => {
      // Try landing.pricing.{plan}.pachete.{featureCode} first
      const landingPacheteKey = `landing.pricing.${plan}.pachete.${featureCode}`;
      const landingPacheteTranslation = t(landingPacheteKey) || '';
      if (landingPacheteTranslation && landingPacheteTranslation !== landingPacheteKey) {
        return landingPacheteTranslation;
      }
      
      // Fallback to pricing.{plan}.pachete.{featureCode}
      const pacheteKey = `pricing.${plan}.pachete.${featureCode}`;
      const pacheteTranslation = t(pacheteKey) || '';
      if (pacheteTranslation && pacheteTranslation !== pacheteKey) {
        return pacheteTranslation;
      }
      
      // Last fallback: return the feature code as-is
      return featureCode;
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <Link to="/" className="flex items-center flex-shrink-0 overflow-visible">
              <span className="font-bold font-logo bg-gradient-to-r from-pink-500 to-rose-500 bg-clip-text text-transparent whitespace-nowrap overflow-visible" style={{ fontSize: '36px', lineHeight: '40px', display: 'inline-block', paddingLeft: '5px', paddingRight: '0' }}>
                {t('header.logo')}
              </span>
            </Link>
            <div className="flex items-center gap-4">
              <LanguageSwitcher compact />
              <Button 
                onClick={handleLogin}
                className="gradient-romantic text-white hover:opacity-90 h-10 px-4 py-2 w-full sm:w-auto"
              >
                {isAuthenticated ? t('navigation.events') : t('header.login')}
              </Button>
              {isAuthenticated && user && (
                <UserProfileDropdown userName={user.user_name} isAdmin={Boolean(user.user_admin)} />
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden gradient-romantic text-white py-12 sm:py-16 lg:py-0 lg:h-[597px] lg:flex lg:items-center">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="text-center lg:text-left">
              <h1
                className="text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl"
                style={{ lineHeight: "1.05" }}
              >
                {t("landing.title")}
              </h1>
              <p className="mt-5 text-lg leading-8 text-white/90 sm:text-xl">
                {t("landing.subtitle")}
              </p>
              <div className="mt-8 flex items-center justify-center lg:justify-start gap-x-4">
                <Button
                  onClick={handleGetStarted}
                  size="lg"
                  className="rounded-full bg-white text-pink-600 hover:bg-white/90 hover:text-pink-700 text-base sm:text-lg px-7 sm:px-9 py-5 h-auto shadow-romantic"
                >
                  {t("landing.hero.cta")}
                </Button>
              </div>
            </div>

            <div className="relative mx-auto w-full max-w-xl lg:max-w-none">
              {/* Decorative accents (outlined heart + small star) */}
              <div className="pointer-events-none select-none absolute -top-2 right-4 sm:right-10 text-white/35">
                <Heart className="h-14 w-14 sm:h-16 sm:w-16" />
              </div>
              <div className="pointer-events-none select-none absolute top-24 right-10 sm:right-14 text-amber-300/80">
                <Star className="h-4 w-4 fill-current" />
              </div>

              <div className="relative">
                {/* Tablet mockup (with rounded white frame) */}
                <div className="relative z-10 mx-auto w-[50%] sm:w-[45%] lg:w-[38.4%] -translate-x-8 lg:-translate-x-16 rotate-[4deg]">
                  <div className="rounded-[40px] bg-white p-3 shadow-[0_24px_50px_rgba(0,0,0,0.18)]">
                    <div className="overflow-hidden rounded-[32px]">
                      <img
                        src={heroTabletMockup}
                        alt={t("landing.hero.tabletAlt")}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>

                {/* Mobile mockup (slightly lower, with rounded white frame, 100px gap from tablet) */}
                <div className="absolute z-20 top-28 right-4 sm:right-12 lg:left-[calc(50%+19.2%+36px)] w-[21%] rotate-[1deg]">
                  <div className="rounded-[30px] bg-white p-2 shadow-[0_18px_36px_rgba(0,0,0,0.18)]">
                    <div className="overflow-hidden rounded-[24px]">
                      <img
                        src={heroMobileMockup}
                        alt={t("landing.hero.mobileAlt")}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>

                {/* Pills (white pill badges with icons) */}
                <div className="pointer-events-none select-none absolute z-30 top-5 right-8 sm:right-16 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-foreground shadow-soft">
                  <Gift className="h-4 w-4 text-pink-600" />
                  {t("landing.hero.badgeGifts")}
                </div>
                <div className="pointer-events-none select-none absolute z-30 bottom-10 right-0 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-foreground shadow-soft">
                  <Mail className="h-4 w-4 text-pink-600" />
                  {t("landing.hero.badgeRsvp")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bullets row (pale strip under hero) */}
      <section className="py-4 sm:py-6" style={{ backgroundColor: "rgba(249,236,238,0.6)" }}>
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-[rgb(132,98,109)]">
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-pink-600" />
              <span>{t("landing.trust.securePayments")}</span>
            </div>
            <span className="opacity-60">•</span>
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-pink-600" />
              <span>{t("landing.trust.noAccount")}</span>
            </div>
            <span className="opacity-60">•</span>
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-pink-600" />
              <span>{t("landing.trust.fullControl")}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Recommended By Section */}
      <section className="py-10" style={{ backgroundColor: "rgba(249,236,238,0.6)" }}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center justify-center gap-6">
            <h3 className="text-xl sm:text-2xl font-display font-semibold text-foreground">
              {t('landing.recommended.title')}
            </h3>
            <div className="flex flex-wrap items-center justify-center gap-10 sm:gap-12">
              <a 
                href="https://www.libertatea.ro/publicitate-advertorial/cadoul-de-nunta-sau-de-botez-se-da-acum-cu-cardul-si-e-perfect-normal-5338453"
                target="_blank" 
                rel="noopener noreferrer" 
                className="group opacity-70 hover:opacity-100 focus-visible:opacity-100 transition-all duration-200 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <img 
                  src="/logo-libertatea-CFcKJVPx.png" 
                  alt="Libertatea" 
                  className="h-10 sm:h-11 w-auto transition-transform duration-200 group-hover:scale-[1.04] group-focus-visible:scale-[1.04] grayscale"
                />
              </a>
              <a 
                href="https://www.elle.ro/advertorial/ce-se-intampla-cand-planificarea-nuntii-sau-a-botezului-devine-o-bucurie-nu-un-haos-un-link-poate-schimba-totul-1015918"
                target="_blank" 
                rel="noopener noreferrer" 
                className="group opacity-70 hover:opacity-100 focus-visible:opacity-100 transition-all duration-200 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <img 
                  src="/logo-elle-DO8uRWwd.png" 
                  alt="ELLE" 
                  className="h-10 sm:h-11 w-auto transition-transform duration-200 group-hover:scale-[1.04] group-focus-visible:scale-[1.04] grayscale"
                />
              </a>
              <a 
                href="https://www.unica.ro/advertorial/organizarea-unei-nunti-in-2025-un-site-o-invitatie-si-tot-ce-trebuie-sa-stie-invitatii-tai-745086"
                target="_blank" 
                rel="noopener noreferrer" 
                className="group opacity-70 hover:opacity-100 focus-visible:opacity-100 transition-all duration-200 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <img 
                  src="/logo-unica-Df1uoJ9Q.png" 
                  alt="Unica" 
                  className="h-10 sm:h-11 w-auto transition-transform duration-200 group-hover:scale-[1.04] group-focus-visible:scale-[1.04] grayscale"
                />
              </a>
              <a 
                href="https://www.viva.ro/publicitate/tap-to-gift-cum-se-schimba-eticheta-darului-de-nunta-sau-de-botez-in-era-digitala-3698447"
                target="_blank" 
                rel="noopener noreferrer" 
                className="group opacity-70 hover:opacity-100 focus-visible:opacity-100 transition-all duration-200 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <img 
                  src="/logo-viva-DktRSDM6.png" 
                  alt="VIVA!" 
                  className="h-10 sm:h-11 w-auto transition-transform duration-200 group-hover:scale-[1.04] group-focus-visible:scale-[1.04] grayscale"
                />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Core Value Section */}
      <section className="relative py-12 sm:py-16 overflow-hidden" style={{ backgroundColor: "rgba(249,236,238,0.6)" }}>
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              {t("landing.value.title")}
            </h2>
            <p className="mt-4 text-base sm:text-lg leading-7 text-slate-600">
              {t("landing.value.subtitle")}
            </p>
          </div>

          <div className="mx-auto mt-8 max-w-5xl grid grid-cols-1 gap-6 md:grid-cols-3">
            <div className="rounded-2xl bg-white p-6 shadow-soft border border-border/50">
              <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-pink-100">
                <Tablet className="h-5 w-5 text-pink-600" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">
                {t("landing.value.cards.publicPage.title")}
              </h3>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                {t("landing.value.cards.publicPage.body")}
              </p>
            </div>

            <div className="rounded-2xl bg-white p-6 shadow-soft border border-border/50">
              <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-pink-100">
                <Gift className="h-5 w-5 text-pink-600" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">
                {t("landing.value.cards.gifts.title")}
              </h3>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                {t("landing.value.cards.gifts.body")}
              </p>
            </div>

            <div className="rounded-2xl bg-white p-6 shadow-soft border border-border/50">
              <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-pink-100">
                <Users className="h-5 w-5 text-pink-600" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">
                {t("landing.value.cards.rsvp.title")}
              </h3>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                {t("landing.value.cards.rsvp.body")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Public page section */}
      <section className="py-12 sm:py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              {t("landing.publicPage.title")}
            </h2>
            <p className="mt-4 text-base sm:text-lg leading-7 text-[#84626d]">
              {t("landing.publicPage.subtitle")}
            </p>
            <ul className="mx-auto mt-6 inline-flex max-w-xl flex-col items-start space-y-3 text-left text-sm text-[#84626d]">
              {[
                t("landing.publicPage.bullets.oneLink"),
                t("landing.publicPage.bullets.rsvpAndGifts"),
                t("landing.publicPage.bullets.noAccount"),
                t("landing.publicPage.bullets.mobile"),
              ].map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-pink-100">
                    <Check className="h-3.5 w-3.5 text-pink-600" />
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Button onClick={handleViewExample} variant="outline" className="border-pink-200 hover:bg-pink-50">
                {t("landing.publicPage.cta")}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Simple for guests */}
      <section className="py-12 sm:py-16" style={{ backgroundColor: "rgba(249,236,238,0.6)" }}>
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              {t("landing.guestsSimple.title")}
            </h2>
          </div>
          <div className="mx-auto mt-8 max-w-4xl grid grid-cols-2 gap-4 sm:grid-cols-4">
            {[
              t("landing.guestsSimple.steps.linkQr"),
              t("landing.guestsSimple.steps.confirm"),
              t("landing.guestsSimple.steps.gift"),
              t("landing.guestsSimple.steps.noApps"),
            ].map((label) => (
              <div key={label} className="rounded-2xl bg-white p-5 text-center shadow-soft border border-border/50">
                <div className="mx-auto mb-3 h-10 w-10 rounded-full bg-pink-100 flex items-center justify-center">
                  <Heart className="h-5 w-5 text-pink-600" />
                </div>
                <p className="text-sm text-muted-foreground leading-6">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 sm:py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              {t('landing.testimonials.title')}
            </h2>
            <p className="mt-6 text-lg leading-8 text-[#84626d]">
              {t('landing.testimonials.subtitle')}
            </p>
          </div>
          <div className="mx-auto mt-8 max-w-5xl grid grid-cols-1 gap-8 md:grid-cols-2">
            {/* Testimonial 1 */}
            <div className="p-8">
              <div className="flex items-center gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-pink-500 text-pink-500" />
                ))}
              </div>
              <p className="font-sans italic text-[#84626d] mb-6 leading-relaxed text-base sm:text-lg">
                "{t('landing.testimonials.mariaAlexandru.quote')}"
              </p>
              <p className="font-semibold text-foreground">
                {t('landing.testimonials.mariaAlexandru.couple')}
              </p>
            </div>

            {/* Testimonial 2 */}
            <div className="p-8">
              <div className="flex items-center gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-pink-500 text-pink-500" />
                ))}
              </div>
              <p className="font-sans italic text-[#84626d] mb-6 leading-relaxed text-base sm:text-lg">
                "{t('landing.testimonials.anaMihai.quote')}"
              </p>
              <p className="font-semibold text-foreground">
                {t('landing.testimonials.anaMihai.couple')}
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-12 sm:py-16 bg-gradient-soft">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              {t('landing.howItWorks.title')}
            </h2>
            <p className="mt-6 text-lg leading-8 text-muted-foreground">
              {t('landing.howItWorks.subtitle')}
            </p>
          </div>
          <div className="mx-auto mt-8 max-w-5xl grid grid-cols-1 gap-8 md:grid-cols-3">
            {/* Step 1 */}
            <div className="flex flex-col items-center text-center">
              <div className="relative mb-6 w-20 h-20">
                {/* Large gradient circle with number */}
                <div className="w-20 h-20 gradient-romantic rounded-full flex items-center justify-center text-white font-bold text-2xl">
                  01
                </div>
                {/* Small white circle with icon on top */}
                <div className="absolute -bottom-3 -right-3 z-10 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg">
                  <Calendar className="h-6 w-6 text-pink-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-foreground">
                {t('landing.howItWorks.step1.title')}
              </h3>
              <p className="mt-4 text-muted-foreground">
                {t('landing.howItWorks.step1.description')}
              </p>
            </div>

            {/* Step 2 */}
            <div className="flex flex-col items-center text-center">
              <div className="relative mb-6 w-20 h-20">
                {/* Large gradient circle with number */}
                <div className="w-20 h-20 gradient-romantic rounded-full flex items-center justify-center text-white font-bold text-2xl">
                  02
                </div>
                {/* Small white circle with icon on top */}
                <div className="absolute -bottom-3 -right-3 z-10 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg">
                  <Send className="h-6 w-6 text-pink-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-foreground">
                {t('landing.howItWorks.step2.title')}
              </h3>
              <p className="mt-4 text-muted-foreground">
                {t('landing.howItWorks.step2.description')}
              </p>
            </div>

            {/* Step 3 */}
            <div className="flex flex-col items-center text-center">
              <div className="relative mb-6 w-20 h-20">
                {/* Large gradient circle with number */}
                <div className="w-20 h-20 gradient-romantic rounded-full flex items-center justify-center text-white font-bold text-2xl">
                  03
                </div>
                {/* Small white circle with icon on top */}
                <div className="absolute -bottom-3 -right-3 z-10 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg">
                  <ListChecks className="h-6 w-6 text-pink-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-foreground">
                {t('landing.howItWorks.step3.title')}
              </h3>
              <p className="mt-4 text-muted-foreground">
                {t('landing.howItWorks.step3.description')}
              </p>
            </div>
          </div>
          <div className="mt-10 flex justify-center">
            <Button onClick={handleGetStarted} className="gradient-romantic text-white px-6">
              <Heart className="mr-2 h-4 w-4" />
              {t("landing.howItWorks.cta")}
            </Button>
          </div>
        </div>
      </section>

      {/* Payments / Stripe section */}
      <section className="py-12 sm:py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-5xl grid grid-cols-1 gap-10 md:grid-cols-2 items-center">
            <div>
              <div className="mx-auto max-w-sm rounded-2xl bg-gradient-to-b from-pink-50 to-white p-6 shadow-soft">
                <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-medium text-foreground shadow-sm">
                  <Lock className="h-4 w-4 text-pink-600" />
                  {t("landing.payments.badge")}
                </div>

                {/* Lightweight "logo grid" layout */}
                <div className="mt-5 grid grid-cols-3 gap-3">
                  {["VISA", "Mastercard", "Apple Pay", "G Pay", "Stripe", "3D Secure"].map((label) => (
                    <div
                      key={label}
                      className="h-10 rounded-xl bg-white flex items-center justify-center text-xs font-semibold text-slate-700 shadow-sm"
                    >
                      {label}
                    </div>
                  ))}
                </div>

                <div className="mt-4 flex items-center justify-center">
                  <span className="text-xs text-muted-foreground">
                    {t("landing.payments.poweredBy")}
                  </span>
                </div>

                {/* Payment methods strip asset */}
                <div className="mt-4 flex justify-center">
                  <img
                    src={heroPaymentsStrip}
                    alt={t("landing.payments.methodsAlt")}
                    className="h-5 opacity-70"
                  />
                </div>
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                {t("landing.payments.title")}
              </h2>
              <p className="mt-4 text-base sm:text-lg leading-7 text-muted-foreground">
                {t("landing.payments.subtitle")}
              </p>
              <ul className="mt-6 space-y-3 text-sm text-muted-foreground">
                {[
                  t("landing.payments.bullets.processors"),
                  t("landing.payments.bullets.noGuestData"),
                  t("landing.payments.bullets.clear"),
                  t("landing.payments.bullets.withdrawals"),
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-pink-100">
                      <Check className="h-3.5 w-3.5 text-pink-600" />
                    </span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              {t('landing.pricing.title')}
            </h2>
            <p className="mt-6 text-lg leading-8 text-muted-foreground">
              {t("landing.pricing.subtitle")}
            </p>
            {/* JUB-27: per-event pricing model, stated next to the prices. */}
            <p
              className="mt-4 inline-block rounded-full border border-pink-200 bg-pink-50 px-4 py-2 text-sm font-medium text-pink-900"
              data-testid="landing-pricing-per-event-note"
            >
              {t("landing.pricing.perEventNote")}
            </p>
          </div>
          <div className="mx-auto mt-8 max-w-5xl grid grid-cols-1 gap-8 md:grid-cols-3">
            {/* Free Plan */}
            <div className="rounded-2xl bg-card p-8 shadow-sm border border-border">
              <div className="text-center">
                <h3 className="text-2xl font-bold text-foreground">
                  {t('landing.pricing.free.name')}
                </h3>
                <div className="mt-4 flex items-baseline justify-center gap-x-2">
                  <span className="text-4xl font-bold tracking-tight text-foreground">
                    {getPlanPrice('free')}
                  </span>
                </div>
                <p className="mt-4 text-sm leading-6 text-muted-foreground">
                  {t('landing.pricing.free.description')}
                </p>
              </div>
              <ul className="mt-8 space-y-3 text-sm leading-6 text-muted-foreground">
                {getPlanFeatures('free').map((feature: string, index: number) => (
                  <li key={index} className="flex gap-x-3">
                    <Check className="h-6 w-6 flex-none text-pink-600" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <Button 
                onClick={handleGetStarted}
                variant="outline" 
                className="mt-8 w-full"
              >
                {t('landing.pricing.free.cta')}
              </Button>
            </div>

            {/* Standard Plan */}
            <div className="rounded-2xl bg-card p-8 shadow-lg border-2 border-pink-500 relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="rounded-full bg-pink-600 px-3 py-1 text-xs font-semibold text-white">
                  {t('landing.pricing.standard.popular')}
                </span>
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-bold text-foreground">
                  {t('landing.pricing.standard.name')}
                </h3>
                <div className="mt-4 flex items-baseline justify-center gap-x-2">
                  <span className="text-4xl font-bold tracking-tight text-foreground">
                    {getPlanPrice('standard')}
                  </span>
                </div>
                <p className="mt-4 text-sm leading-6 text-muted-foreground">
                  {t('landing.pricing.standard.description')}
                </p>
              </div>
              <ul className="mt-8 space-y-3 text-sm leading-6 text-muted-foreground">
                {getPlanFeatures('standard').map((feature: string, index: number) => (
                  <li key={index} className="flex gap-x-3">
                    <Check className="h-6 w-6 flex-none text-pink-600" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => handleSelectPlan('standard')}
                className="mt-8 w-full gradient-romantic text-white"
              >
                {t('landing.pricing.standard.cta')}
              </Button>
            </div>

            {/* Premium Plan */}
            <div className="rounded-2xl bg-card p-8 shadow-sm border border-border">
              <div className="text-center">
                <h3 className="text-2xl font-bold text-foreground">
                  {t('landing.pricing.premium.name')}
                </h3>
                <div className="mt-4 flex items-baseline justify-center gap-x-2">
                  <span className="text-4xl font-bold tracking-tight text-foreground">
                    {getPlanPrice('premium')}
                  </span>
                </div>
                <p className="mt-4 text-sm leading-6 text-muted-foreground">
                  {t('landing.pricing.premium.description')}
                </p>
              </div>
              <ul className="mt-8 space-y-3 text-sm leading-6 text-muted-foreground">
                {getPlanFeatures('premium').map((feature: string, index: number) => (
                  <li key={index} className="flex gap-x-3">
                    <Check className="h-6 w-6 flex-none text-pink-600" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <Button 
                onClick={handleGetStarted}
                variant="outline" 
                className="mt-8 w-full"
              >
                {t('landing.pricing.premium.cta')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              {t("landing.faq.title")}
            </h2>
          </div>
          <div className="mx-auto mt-8 max-w-3xl divide-y divide-border rounded-2xl border border-border bg-white">
            {faqItems.map((item) => (
              <details key={item.q} className="group px-5 py-4">
                <summary className="cursor-pointer list-none font-display font-medium text-foreground flex items-center justify-between gap-4">
                  <span>{item.q}</span>
                  <span className="text-muted-foreground group-open:rotate-180 transition-transform">⌄</span>
                </summary>
                <p className="mt-3 text-sm leading-6 text-[#84626d]">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section className="py-16" style={{ backgroundImage: 'linear-gradient(135deg, rgb(226, 54, 112), rgb(224, 108, 185))' }}>
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-balance text-white sm:text-4xl">
              {t("landing.bottomCta.title")}
            </h2>
            <p className="mt-4 text-lg leading-8 text-white/90">
              {t("landing.bottomCta.subtitle")}
            </p>
            <div className="mt-8 flex items-center justify-center">
              <Button
                onClick={handleGetStarted}
                size="lg"
                className="bg-white text-pink-600 hover:bg-white/90 hover:text-pink-700 text-base sm:text-lg px-8 py-6 h-auto shadow-romantic"
              >
                <Heart className="mr-2 h-5 w-5" />
                {t("landing.bottomCta.button")}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t" data-build="artifact-v1.0" style={{ backgroundImage: 'linear-gradient(to right bottom, rgb(255, 250, 250), rgba(249, 236, 238, 0.2))', borderTopColor: 'rgba(237, 222, 227, 0.5)' }}>
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {/* Logo & Mission */}
            <div>
              <h3 className="font-bold font-logo bg-gradient-to-r from-pink-500 to-rose-500 bg-clip-text text-transparent mb-4 whitespace-nowrap overflow-visible" style={{ fontSize: '36px', lineHeight: '40px', display: 'inline-block', paddingLeft: '5px', paddingRight: '0' }}>
                {t('header.logo')}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('footer.mission')}
              </p>
            </div>

            <FooterLinkColumns />
          </div>

          {/* Bottom Bar */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex flex-col gap-1">
                <p className="text-sm text-muted-foreground">
                  {t('footer.copyright', { year: String(new Date().getFullYear()) })}
                </p>
              </div>
              {/* Payment Method Image */}
              <div className="flex items-center">
                <img 
                  src="/payment-methods.png" 
                  alt="Payment methods" 
                  className="h-6 opacity-60"
                />
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
